import { useContext, createContext, useMemo, useState } from "react";
import DataGrid, {
  Column,
  HeaderRendererProps,
  SortColumn,
  useFocusRef,
} from "react-data-grid";
import {
  getComparator,
  dataGridTabClassname,
  inputStopPropagation,
  filterClassname,
  styleDataGrid,
  filterColumnClassName,
  filterCellClass,
  // selectStopPropagation,
} from "../../datagrid/extra";
import EmptyRowsRenderer from "../../components/EmptyRow";
import { format } from "date-fns";
import { ProductLibAppIAM } from "../../API";
import Icon from "../../components/Icon";
import { Link } from "react-router-dom";
import FilterRowsRenderer from "../../components/Filter";

interface Filter extends Omit<ProductLibAppIAM, "id"> {
  id: string;
  enabled: boolean;
}

const FilterContext = createContext<Filter | undefined>(undefined);

interface SummaryRow {
  id: string;
  totalCount: number;
}

const dateFormat = "dd-MM-yyyy (HH:mm:ss)";

function Table({ data }: any) {
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([
    { columnKey: "updatedAt", direction: "DESC" },
  ]);

  const [filters, setFilters] = useState<Filter>({
    __typename: "ProductLibAppIAM",
    id: "",
    username: "",
    name: "",
    email: "",
    role: "",
    createdAt: "",
    updatedAt: "",
    enabled: false,
  });

  const filteredRows = useMemo(() => {
    return data.filter((r: any) => {
      return (
        (filters.id
          ? r.id
            ? r.id.toLocaleLowerCase().includes(filters.id.toLowerCase())
            : false
          : true) &&
        (filters.username
          ? r.username
            ? r.username
                .toLocaleLowerCase()
                .includes(filters.username.toLowerCase())
            : false
          : true) &&
        (filters.name
          ? r.name
            ? r.name.toLocaleLowerCase().includes(filters.name.toLowerCase())
            : false
          : true) &&
        (filters.email
          ? r.email
            ? r.email.toLocaleLowerCase().includes(filters.email.toLowerCase())
            : false
          : true) &&
        (filters.role
          ? r.role
            ? r.role.toLocaleLowerCase().includes(filters.role.toLowerCase())
            : false
          : true) &&
        (filters.createdAt
          ? r.createdAt
            ? format(new Date(r.createdAt), dateFormat)
                .toLocaleLowerCase()
                .includes(filters.createdAt.toLowerCase())
            : false
          : true) &&
        (filters.updatedAt
          ? r.updatedAt
            ? format(new Date(r.updatedAt), dateFormat)
                .toLocaleLowerCase()
                .includes(filters.updatedAt.toLowerCase())
            : false
          : true)
      );
    });
  }, [data, filters]);

  const ControlPanelColumn: Column<ProductLibAppIAM, SummaryRow>[] = [
    {
      key: "id",
      name: "ID",
      width: 80,
      frozen: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => (
        <Link
          to={`/userPermission/${row.id}/update`}
          className={" text-sky-400 hover:text-sky-600 font-bold"}
        >
          {row.id.substring(row.id.length - 6)}
        </Link>
      ),
      summaryFormatter({ row }) {
        return (
          <div className="opacity-50">
            <strong>{row.totalCount} Users</strong>
          </div>
        );
      },
    },
    {
      key: "username",
      name: "Username",
      width: 300,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,

      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.username ? filters.username : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  username: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "name",
      name: "Name",
      width: 170,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,

      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.name ? filters.name : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  name: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },

    {
      key: "email",
      name: "Email",
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.email ? filters.email : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  email: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "role",
      name: "Role",
      width: 100,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.role ? filters.role : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  role: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "updatedAt",
      name: "Updated At",
      width: 170,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.updatedAt), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => <></>}
        </FilterRenderer>
      ),
    },
    {
      key: "createdAt",
      name: "Created At",
      width: 170,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.createdAt), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibAppIAM, SummaryRow, HTMLInputElement> {...p}>
          {({ filters, ...rest }) => <></>}
        </FilterRenderer>
      ),
    }
  ];

  function clearFilters() {
    setFilters({
      __typename: "ProductLibAppIAM",
      id: "",
      username: "",
      name: "",
      email: "",
      role: "",
      createdAt: "",
      updatedAt: "",
      enabled: false,
    });
  }

  function toggleFilters() {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  }

  const sortedRows = useMemo((): readonly ProductLibAppIAM[] => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const summaryRows = useMemo(() => {
    const summaryRow: SummaryRow = {
      id: "total_0",
      totalCount: sortedRows.length,
    };
    return [summaryRow];
  }, [sortedRows]);

  return (
    <>
    <div className="flex flex-wrap items-center justify-end mb-3">
    <a
          href="/userPermission/new"
          className="inline-block rounded-full bg-primary-100 px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-primary-700 transition duration-150 ease-in-out hover:bg-primary-accent-100 focus:bg-primary-accent-100 focus:outline-none focus:ring-0 active:bg-primary-accent-200"
        >
          New Permission
        </a>
    </div>
      <div className="flex flex-wrap items-center justify-between mb-3">
        <FilterRowsRenderer
          toggleFilters={toggleFilters}
          clearFilters={clearFilters}
          activeFilters={filters}
        />
      </div>
      <FilterContext.Provider value={filters}>
        <DataGrid
          columns={ControlPanelColumn}
          rows={sortedRows}
          bottomSummaryRows={summaryRows}
          renderers={{ noRowsFallback: <EmptyRowsRenderer /> }}
          headerRowHeight={filters.enabled ? 100 : 50}
          sortColumns={sortColumns}
          onSortColumnsChange={setSortColumns}
          className={dataGridTabClassname}
        />
      </FilterContext.Provider>
      <style>{styleDataGrid}</style>
    </>
  );
}
function FilterRenderer<R, SR, T extends HTMLOrSVGElement>({
  isCellSelected,
  onSort,
  sortDirection,
  column,
  children,
}: HeaderRendererProps<R, SR> & {
  children: (args: {
    ref: React.RefObject<T>;
    tabIndex: number;
    filters: Filter;
  }) => React.ReactElement;
}) {
  const filters = useContext(FilterContext)!;
  const { ref, tabIndex } = useFocusRef<T>(isCellSelected);
  return (
    <>
      <div
        className="flex flex-row"
        onClick={() => {
          onSort(false);
        }}
      >
        <div className="flex flex-row mt-1 ml-2 mr-auto text-truncate">
          <span>{column.name}</span>
        </div>
        <div className="mt-5">
          {sortDirection === "ASC" && <Icon name="sort-alpha-down" />}
          {sortDirection === "DESC" && <Icon name="sort-alpha-up" />}
          {sortDirection !== "DESC" && sortDirection !== "ASC" && (
            <Icon name="arrows-alt-v" />
          )}
        </div>
      </div>
      {filters.enabled && (
        <div className="">{children({ ref, tabIndex, filters })}</div>
      )}
    </>
  );
}
export default Table;
